import React, { FormEvent, useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { useCorporateContext } from '../CorporateContext';
import { useValidateOfferCode } from '../hooks/useValidateOfferCode';
import { CorporateAudience } from '../types';

const MIN_CODE_LENGTH = 6;

// Banner height on desktop: 10px padding × 2 + 20px line + 1px border.
const DESKTOP_ERROR_HEIGHT = 41;

type CodeError = 'notRecognized' | 'expired' | 'suspended' | 'notFound';

type Props = {
  monthPeriod: string | undefined;
  withForm: boolean;
  notFound: boolean;
};

const getErrorMessage = (error: CodeError, accountName: string) => {
  switch (error) {
    case 'expired':
      return {
        title: 'This code has expired.',
        text: `Ask ${accountName} for a new access code.`,
      };
    case 'suspended':
      return {
        title: 'This offer is no longer available.',
        text: `Contact ${accountName} for more details.`,
      };
    case 'notFound':
      return { title: 'We’re sorry, this offer no longer exists.', text: '' };
    default:
      return {
        title: 'We don’t recognise that code.',
        text: `Check it matches the code from ${accountName} and try again.`,
      };
  }
};

const GroupOfferHeader = ({ monthPeriod, withForm, notFound }: Props) => {
  const { images, accountName, audience } = useCorporateContext();

  const [code, setCode] = useState('');
  const [codeError, setError] = useState<CodeError | null>(null);
  const [isLogoVisible, setIsLogoVisible] = useState(true);
  const [isPinned, setIsPinned] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const formRowRef = useRef<HTMLDivElement>(null);

  // Tablet and phone: only the form row is sticky, shade it once it pins
  // and slide it away while scrolling down, back in on scroll up.
  useEffect(() => {
    const row = formRowRef.current;

    if (!row) {
      return;
    }

    let lastY = window.scrollY;

    const pin = () => {
      const pinned =
        getComputedStyle(row).position === 'sticky' &&
        row.getBoundingClientRect().top <= 0;
      const delta = window.scrollY - lastY;

      setIsPinned(pinned);
      if (!pinned || row.contains(document.activeElement)) {
        setIsHidden(false);
      } else if (Math.abs(delta) > 5) {
        setIsHidden(delta > 0);
      }
      lastY = window.scrollY;
    };

    window.addEventListener('scroll', pin, { passive: true });
    window.addEventListener('resize', pin);
    pin();

    return () => {
      window.removeEventListener('scroll', pin);
      window.removeEventListener('resize', pin);
    };
  }, [withForm]);

  const { mutate: validateCode, isLoading } = useValidateOfferCode({
    onExpired: () => setError('expired'),
    onSuspended: () => setError('suspended'),
    onNotRecognized: () => setError('notRecognized'),
  });

  const error = notFound ? 'notFound' : codeError;
  const errorMessage = error && getErrorMessage(error, accountName);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    validateCode(code.trim());
  };

  const errorContent = errorMessage && (
    <>
      <ErrorIcon />
      <ErrorText>
        <strong>{errorMessage.title}</strong> {errorMessage.text}
      </ErrorText>
    </>
  );

  if (notFound) {
    return withForm ? (
      <NotFoundBanner role="alert">{errorContent}</NotFoundBanner>
    ) : null;
  }

  const hasError = withForm && !!error;

  return (
    <>
      <ErrorSpacer $hasError={hasError} />
      <Container $hasError={hasError}>
        <Intro>
          {!!images.logo && isLogoVisible && (
            <Logo
              src={images.logo}
              alt={accountName}
              onError={() => setIsLogoVisible(false)}
            />
          )}
          {!!monthPeriod && <Badge>{monthPeriod} free!</Badge>}
          {!!accountName && (
            <Text>
              {audience === CorporateAudience.Employee
                ? `Your membership is covered by ${accountName}, no payment needed.`
                : `Shido is a member benefit included with ${accountName}, no payment needed.`}
            </Text>
          )}
        </Intro>
        {withForm && (
          <FormRow ref={formRowRef} $isPinned={isPinned} $isHidden={isHidden}>
            {!!errorContent && (
              <ErrorBanner role="alert">{errorContent}</ErrorBanner>
            )}
            <Form onSubmit={onSubmit} $hasError={!!error}>
              <Input
                value={code}
                placeholder="Enter access code"
                aria-label="Access code"
                aria-invalid={!!error}
                onChange={(event) => {
                  setCode(event.target.value);
                  setError(null);
                }}
              />
              <ApplyButton
                type="submit"
                disabled={code.trim().length < MIN_CODE_LENGTH || isLoading}
              >
                Apply
              </ApplyButton>
            </Form>
          </FormRow>
        )}
      </Container>
    </>
  );
};

const ErrorIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#A3261C"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const Container = styled.header<{ $hasError: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 80px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 4px 16px rgb(0 0 0 / 8%);

  ${({ $hasError }) =>
    $hasError &&
    css`
      height: ${80 + DESKTOP_ERROR_HEIGHT}px;
      padding-top: ${DESKTOP_ERROR_HEIGHT}px;
    `}

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: contents;
  }
`;

// Desktop: the header is fixed, so push the page down by the banner height
// to keep the hero clear of it. Mobile keeps the banner in flow.
const ErrorSpacer = styled.div<{ $hasError: boolean }>`
  height: ${({ $hasError }) => ($hasError ? DESKTOP_ERROR_HEIGHT : 0)}px;
  transition: height 0.2s;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: none;
  }
`;

const Intro = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  max-width: 1400px;
  width: 100%;

  ${({ theme }) => theme.breakpoints.down('md')} {
    flex-direction: column;
    gap: 16px;
    padding: 20px 20px 0;
    box-sizing: border-box;
    text-align: center;
    background-color: ${({ theme }) => theme.colors.white};
  }
`;

const FormRow = styled.div<{ $isPinned: boolean; $isHidden: boolean }>`
  position: relative;
  flex-shrink: 0;

  ${({ theme }) => theme.breakpoints.down('md')} {
    position: sticky;
    top: 0;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 20px;
    background-color: ${({ theme }) => theme.colors.white};
    box-shadow: 0 4px 16px rgb(0 0 0 / 8%);
    transition:
      background-color 0.25s,
      transform 0.25s;

    ${({ $isHidden }) =>
      $isHidden &&
      css`
        transform: translateY(calc(-100% - 20px));
      `}

    ${({ $isPinned }) =>
      $isPinned &&
      css`
        background-color: ${({ theme }) => theme.colors.sand};
      `}
  }
`;

const Logo = styled.img`
  max-width: 120px;
  height: 44px;
  object-fit: contain;
`;

const sheen = keyframes`
  0% {
    transform: translateX(-120%) skewX(-20deg);
  }

  38%,
  100% {
    transform: translateX(260%) skewX(-20deg);
  }
`;

const Badge = styled.span`
  position: relative;
  overflow: hidden;
  isolation: isolate;
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 2px;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.topicBlue};

  &::after {
    content: '';
    position: absolute;
    inset: -50% auto -50% 0;
    z-index: -1;
    width: 60%;
    background: linear-gradient(
      100deg,
      rgb(255 255 255 / 0%) 0%,
      rgb(255 255 255 / 8%) 30%,
      rgb(255 255 255 / 38%) 50%,
      rgb(255 255 255 / 8%) 70%,
      rgb(255 255 255 / 0%) 100%
    );
    transform: translateX(-120%) skewX(-20deg);
    animation: ${sheen} 4.5s cubic-bezier(0.45, 0, 0.2, 1) 1s infinite;
    pointer-events: none;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
      opacity: 0;
    }
  }
`;

const Text = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 300;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.topicBlue};
`;

const ErrorBanner = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: ${DESKTOP_ERROR_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 24px;
  box-sizing: border-box;
  border-bottom: 1px solid #f0bdb8;
  background-color: #fcedec;

  svg {
    flex-shrink: 0;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    position: static;
    height: auto;
    width: 100%;
    max-width: 320px;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 10px 12px;
    border: 1px solid #f0bdb8;
    border-radius: 6px;

    svg {
      margin-top: 1px;
    }
  }
`;

const NotFoundBanner = styled(ErrorBanner)`
  z-index: 1000;
  justify-content: center;

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-width: none;
    align-items: center;
    justify-content: center;
    padding: 12px 20px;
    border-width: 0 0 1px;
    border-radius: 0;
  }
`;

const ErrorText = styled.span`
  font-size: 15px;
  line-height: 20px;
  color: #6e1a13;

  strong {
    font-weight: 600;
    color: #8c1f17;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: 14px;
  }
`;

const Form = styled.form<{ $hasError: boolean }>`
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 3px;
  border: 2px solid
    ${({ $hasError, theme }) =>
      $hasError ? '#b3261e' : theme.colors.borderBlue};
  border-radius: 30px;
  background-color: ${({ theme }) => theme.colors.white};

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 100%;
    max-width: 320px;
    box-sizing: border-box;
  }
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  width: 160px;
  padding: 0 16px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.topicBlue};

  &::placeholder {
    color: ${({ theme }) => theme.colors.darkGray};
  }
`;

const ApplyButton = styled.button`
  padding: 8px 20px;
  border: none;
  border-radius: 30px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.btnBlue};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.btnBlueHover};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export default GroupOfferHeader;
